import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { shopperApi } from '../lib/shopperApi';
import { ApiError } from '../lib/http';
import { CartItem, ProductItem, SavedCart } from '../types/api';

interface CartState {
  items: CartItem[];
  /** True while signed in: every change is also saved to the account. */
  synced: boolean;
  syncError: string | null;
  addItem: (product: Pick<ProductItem, 'item_id' | 'price' | 'description' | 'image_url'>) => void;
  increment: (inventoryId: string) => void;
  decrement: (inventoryId: string) => void;
  removeItem: (inventoryId: string) => void;
  /** Empties the bag here and, when signed in, in the account too. */
  clearCart: () => void;
  /** After a paid order: the server already took the bought items out, so reload it. */
  completePurchase: () => void;
  /** Replace the bag with the account's saved cart and start saving changes. */
  hydrate: (cart: SavedCart) => void;
  /** Signed out: forget the account's bag on this device. */
  reset: () => void;
}

export const MAX_LINE_QUANTITY = 100;
/** Quick repeated taps on a stepper become one save. */
export const SYNC_DELAY_MS = 300;

const isValidCartItem = (item: CartItem): boolean =>
  typeof item.inventory_id === 'string' &&
  Number.isInteger(item.quantity) &&
  item.quantity > 0 &&
  item.quantity <= MAX_LINE_QUANTITY;

const fromSaved = (cart: SavedCart): CartItem[] =>
  cart.lines.map(({ product, quantity, available_quantity }) => ({
    inventory_id: product.item_id,
    quantity,
    price: Number(product.price),
    description: product.description,
    image_url: product.image_url,
    available_quantity,
  }));

const pending = new Map<string, ReturnType<typeof setTimeout>>();

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => {
      const failed = (error: unknown) => {
        set({ syncError: error instanceof ApiError ? error.message : 'Could not save your bag.' });
        // Show what the account actually holds rather than an unsaved guess, keeping the message.
        void shopperApi.cart().then(
          (cart) => set({ items: fromSaved(cart) }),
          () => undefined,
        );
      };
      const settle = (cart: SavedCart) => {
        // A newer edit is still waiting to be saved; its own response will settle the bag.
        if (pending.size === 0) set({ items: fromSaved(cart), syncError: null });
      };
      /** Save one line's current quantity after a short pause. */
      const save = (inventoryId: string) => {
        if (!get().synced) return;
        clearTimeout(pending.get(inventoryId));
        pending.set(
          inventoryId,
          setTimeout(() => {
            pending.delete(inventoryId);
            const quantity = get().items.find((i) => i.inventory_id === inventoryId)?.quantity ?? 0;
            const request = quantity
              ? shopperApi.setCartQuantity(inventoryId, quantity)
              : shopperApi.removeCartLine(inventoryId);
            request.then(settle, failed);
          }, SYNC_DELAY_MS),
        );
      };
      const change = (inventoryId: string, update: (items: CartItem[]) => CartItem[]) => {
        set((state) => ({ items: update(state.items) }));
        save(inventoryId);
      };
      return {
        items: [],
        synced: false,
        syncError: null,

        addItem: (product) =>
          change(product.item_id, (items) =>
            items.some((i) => i.inventory_id === product.item_id)
              ? items.map((i) =>
                  i.inventory_id === product.item_id
                    ? { ...i, quantity: Math.min(i.quantity + 1, MAX_LINE_QUANTITY) }
                    : i,
                )
              : [
                  ...items,
                  {
                    inventory_id: product.item_id,
                    quantity: 1,
                    price: product.price,
                    description: product.description,
                    image_url: product.image_url,
                  },
                ],
          ),

        increment: (inventoryId) =>
          change(inventoryId, (items) =>
            items.map((i) =>
              i.inventory_id === inventoryId
                ? { ...i, quantity: Math.min(i.quantity + 1, MAX_LINE_QUANTITY) }
                : i,
            ),
          ),

        decrement: (inventoryId) =>
          change(inventoryId, (items) =>
            items
              .map((i) => (i.inventory_id === inventoryId ? { ...i, quantity: i.quantity - 1 } : i))
              .filter((i) => i.quantity > 0),
          ),

        removeItem: (inventoryId) =>
          change(inventoryId, (items) => items.filter((i) => i.inventory_id !== inventoryId)),

        clearCart: () => {
          pending.forEach(clearTimeout);
          pending.clear();
          set({ items: [] });
          if (get().synced) shopperApi.clearCart().catch(failed);
        },

        completePurchase: () => {
          set({ items: [] });
          if (get().synced) shopperApi.cart().then(get().hydrate, () => undefined);
        },

        hydrate: (cart) => set({ items: fromSaved(cart), synced: true, syncError: null }),

        reset: () => {
          pending.forEach(clearTimeout);
          pending.clear();
          set({ items: [], synced: false, syncError: null });
        },
      };
    },
    {
      name: 'rt-cart',
      version: 2,
      partialize: (state) => ({ items: state.items, synced: state.synced }),
      migrate: (persisted) => {
        const old = persisted as Partial<CartState> | undefined;
        return {
          items: (old?.items ?? []).filter(isValidCartItem),
          synced: old?.synced ?? false,
        };
      },
    },
  ),
);

export const selectTotalItems = (state: CartState): number =>
  state.items.reduce((sum, i) => sum + i.quantity, 0);

export const selectQuantity =
  (inventoryId: string) =>
  (state: CartState): number =>
    state.items.find((i) => i.inventory_id === inventoryId)?.quantity ?? 0;
